import type { Dayjs } from "dayjs";
import { generateCalendarDays, getEntryForDate } from "../utils/calendarHelpers";
import { CalendarDayComponent } from "./CalendarDay";
import type { ProfileEntry } from "../hooks/useProfileEntries";
import { useCopy } from "../i18n/locale";

interface CalendarMonthProps {
    month: Dayjs;
    entries: ProfileEntry[];
    selectedDates: Dayjs[];
    onSelectDate: (date: Dayjs, entryId?: string) => void;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function CalendarMonth({ month, entries, selectedDates, onSelectDate }: CalendarMonthProps) {
    const days = generateCalendarDays(month);
    const weekdays = useCopy().weekdays;

    return (
        <div className="p-2 w-full">
            {/* Month header */}
            <div className="mb-6 text-center">
                <h2 className="font-bold text-slate-900 text-3xl">{month.format("MMMM YYYY")}</h2>
            </div>

            {/* Weekday headers */}
            <div className="gap-2 grid grid-cols-7 mb-3">
                {weekdays.map((day) => (
                    <div key={day} className="py-2 font-semibold text-slate-600 text-sm text-center">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="gap-4 grid grid-cols-7">
                {days.map((day, index) => {
                    const entry = getEntryForDate(entries, day.date);
                    const hasEntry = !!entry;
                    const isSubmitted = !!entry?.submitted_at;
                    const isSelected = selectedDates.some((d) => d.isSame(day.date, "day"));

                    return (
                        <CalendarDayComponent
                            key={index}
                            day={day}
                            hasEntry={hasEntry}
                            isSubmitted={isSubmitted}
                            isSelected={isSelected}
                            onSelect={() => day.isCurrentMonth && onSelectDate(day.date, entry?.entry_id)}
                        />
                    );
                })}
            </div>
        </div>
    );
}
